import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AppLayout } from "@/components/AppLayout";
import { motion } from "framer-motion";
import { UserPlus, Trash2, CheckCircle, Clock, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = ({ user }) => {
  const [googleStatus, setGoogleStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    checkGoogleStatus();
    loadTeamMembers();
  }, []);

  const checkGoogleStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/google-status`, { withCredentials: true });
      setGoogleStatus(response.data);
    } catch (error) {
      console.error('Error checking Google status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/team/members`, { withCredentials: true });
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error loading team:', error);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/google/start`, { withCredentials: true });
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error('Could not start Google OAuth');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google OAuth is not configured yet. Ask the admin to fill in GOOGLE_OAUTH_* env vars.');
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/google/disconnect`, {}, { withCredentials: true });
      toast.success('Google account disconnected');
      checkGoogleStatus();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === '1') {
      if (params.get('no_refresh_token') === '1') {
        toast.warning('Connected, but Google did not return a refresh token — access will expire in 1 hour. To fix this permanently: revoke this app at https://myaccount.google.com/permissions, then click Reconnect below.', { duration: 12000 });
      } else {
        toast.success('Google account connected!');
      }
      window.history.replaceState({}, '', window.location.pathname);
      checkGoogleStatus();
    } else if (params.get('google_error')) {
      toast.error(`Google OAuth failed: ${params.get('google_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleSaveTokens = async (e) => {
    e.preventDefault();
    if (!accessToken) { toast.error('Access token is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/google-tokens`, {
        access_token: accessToken,
        refresh_token: refreshToken || undefined,
        client_id: clientId || undefined,
        client_secret: clientSecret || undefined
      }, { withCredentials: true });
      toast.success('Google tokens saved!');
      setAccessToken(''); setRefreshToken(''); setClientId(''); setClientSecret('');
      checkGoogleStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tokens');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/team/invite`, { email: inviteEmail }, { withCredentials: true });
      toast.success(`Invited ${inviteEmail}`);
      setInviteEmail('');
      loadTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (inviteId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/team/members/${inviteId}`, { withCredentials: true });
      toast.success('Member removed');
      loadTeamMembers();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  return (
    <AppLayout user={user} activeNav="/settings">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
        <h2 className="text-2xl font-heading font-extrabold text-stone-900 mb-6">Settings</h2>

        {/* Team Management */}
        <Card className="mb-6 bg-white/90 backdrop-blur-sm border border-stone-200/60">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-sage-600" /> Team Management
            </CardTitle>
            <CardDescription>Invite team members to access your configurations and households</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInvite} className="flex gap-3 mb-4">
              <Input
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="border-2 border-stone-200 rounded-xl focus:border-sage-500 flex-1"
                data-testid="invite-email-input"
                required
              />
              <Button type="submit" disabled={inviting} className="bg-sage-500 hover:bg-sage-600 rounded-xl whitespace-nowrap" data-testid="invite-button">
                <Mail className="w-4 h-4 mr-2" /> {inviting ? 'Inviting...' : 'Invite'}
              </Button>
            </form>

            {teamMembers.length > 0 && (
              <div className="space-y-2">
                {teamMembers.map((member) => (
                  <div key={member.invite_id} className="flex items-center justify-between bg-stone-50 rounded-xl px-4 py-3" data-testid={`team-member-${member.invite_id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${member.status === 'active' ? 'bg-green-100' : 'bg-amber-100'}`}>
                        {member.status === 'active' ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-amber-600" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-700">{member.member_email}</p>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400">{member.status === 'active' ? 'Active' : 'Pending'}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(member.invite_id)} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`remove-member-${member.invite_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {teamMembers.length === 0 && (
              <p className="text-sm text-stone-400 text-center py-4">No team members yet. Invite someone to share access.</p>
            )}
          </CardContent>
        </Card>

        {/* Google Account Connection */}
        <Card className="mb-6 bg-white/90 backdrop-blur-sm border border-stone-200/60">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
              Google Account
            </CardTitle>
            <CardDescription>Connect your Google account for Drive and Sheets access</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-4"><div className="animate-spin rounded-full h-8 w-8 border-2 border-sage-500 border-t-transparent mx-auto"></div></div>
            ) : (
              <div className="space-y-4">
                {googleStatus.connected ? (
                  <Alert className={`rounded-xl ${googleStatus.has_refresh_token === false ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                    <AlertDescription className={`flex items-center justify-between text-sm ${googleStatus.has_refresh_token === false ? 'text-amber-700' : 'text-green-700'}`}>
                      <span className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" /> {googleStatus.message}</span>
                      <button type="button" onClick={handleDisconnectGoogle} className="text-xs hover:underline font-medium ml-2 flex-shrink-0" data-testid="disconnect-google-btn">Disconnect</button>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="bg-amber-50 border-amber-200 rounded-xl">
                    <AlertDescription className="text-amber-700 text-sm">Google account not connected</AlertDescription>
                  </Alert>
                )}

                {/* Primary: real OAuth flow (no manual pasting) */}
                <Button
                  type="button"
                  onClick={handleConnectGoogle}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-sm"
                  data-testid="oauth-connect-google-btn"
                >
                  {googleStatus.connected ? 'Reconnect Google (fresh tokens)' : 'Connect Google'}
                </Button>
                <p className="text-[11px] text-stone-400 text-center -mt-2">Recommended — opens Google to grant access, no manual token pasting.</p>

                {/* Fallback: manual token entry (kept for edge cases) */}
                <details className="border-t border-stone-100 pt-4">
                  <summary className="text-xs text-stone-500 cursor-pointer hover:text-stone-700 select-none">
                    Advanced: Paste tokens manually (fallback)
                  </summary>
                  <form onSubmit={handleSaveTokens} className="space-y-4 mt-3">
                  <div>
                    <Label>Google Access Token *</Label>
                    <Input type="password" placeholder="ya29.a0ARrdaM..." value={accessToken} onChange={(e) => setAccessToken(e.target.value)} className="border-2 border-stone-200 rounded-xl focus:border-sage-500" data-testid="access-token-input" />
                  </div>
                  <div>
                    <Label>Google Refresh Token</Label>
                    <Input type="password" placeholder="1//0gH..." value={refreshToken} onChange={(e) => setRefreshToken(e.target.value)} className="border-2 border-stone-200 rounded-xl focus:border-sage-500" data-testid="refresh-token-input" />
                    <p className="text-xs text-stone-400 mt-1">Recommended for long-term access</p>
                  </div>

                  <div className="border-t border-stone-100 pt-4">
                    <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center justify-between w-full text-left">
                      <div>
                        <p className="font-medium text-stone-700 text-sm">Advanced: Webhook Credentials</p>
                        <p className="text-xs text-stone-400">Required only for Drive Auto-Sync</p>
                      </div>
                      <svg className={`w-4 h-4 text-stone-400 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {showAdvanced && (
                      <div className="mt-4 space-y-4 bg-stone-50 p-4 rounded-xl">
                        <div>
                          <Label>OAuth Client ID</Label>
                          <Input type="password" placeholder="123456789-abc123.apps.googleusercontent.com" value={clientId} onChange={(e) => setClientId(e.target.value)} className="border-2 border-stone-200 rounded-xl focus:border-sage-500" data-testid="client-id-input" />
                        </div>
                        <div>
                          <Label>OAuth Client Secret</Label>
                          <Input type="password" placeholder="GOCSPX-..." value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} className="border-2 border-stone-200 rounded-xl focus:border-sage-500" data-testid="client-secret-input" />
                        </div>
                      </div>
                    )}
                  </div>

                  <Button type="submit" disabled={submitting} className="w-full bg-sage-500 hover:bg-sage-600 rounded-xl" data-testid="save-tokens-button">
                    {submitting ? 'Saving...' : 'Save Google Tokens'}
                  </Button>
                  </form>
                </details>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </AppLayout>
  );
};

export default Settings;
